import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

/**
 * Replaces `User.Identity` in _Layout.cshtml.
 *
 * The Razor layout could read the signed-in user synchronously because the page
 * was rendered server-side. The SPA has to ask, so every consumer gets an
 * `isLoading` flag — rendering the logged-out navbar before the answer arrives
 * would make the header flicker on every full page load.
 */
export interface CurrentUser {
  UserCandidatesID: string
  FullName: string
  Email: string
  Role: string
  /**
   * The number typed at registration, and only for the account that typed it.
   * Empty for everyone else — no upstream endpoint returns a stored phone
   * number, so this is the one chance to offer it back on the profile form.
   */
  PhoneNumber?: string
}

interface AuthState {
  user: CurrentUser | null
  isAuthenticated: boolean
  isLoading: boolean
  /** Re-reads the session — call after login/logout so the navbar updates. */
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthState | undefined>(undefined)

export const authQueryKey = ['account', 'me'] as const

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: authQueryKey,
    queryFn: () => api.get<{ user: CurrentUser | null }>('/api/ecareer/account/me'),
    // The session is 60 minutes; re-checking on every window focus would add
    // nothing but requests.
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const value = useMemo<AuthState>(
    () => ({
      user: data?.user ?? null,
      isAuthenticated: Boolean(data?.user),
      isLoading,
      refresh: async () => {
        await queryClient.invalidateQueries({ queryKey: authQueryKey })
      },
    }),
    [data, isLoading, queryClient],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>')
  return context
}

/**
 * `ViewBag.IsProfileCompleted`. Kept a server call rather than derived on the
 * client: it gates the vacancy pages, and the check is the server's to make.
 */
export function useProfileCompletion(): { isProfileCompleted: boolean; isLoading: boolean } {
  const { isAuthenticated } = useAuth()
  const { data, isLoading } = useQuery({
    queryKey: ['account', 'profile-completion'],
    queryFn: () =>
      api.get<{ isProfileCompleted: boolean }>('/api/ecareer/account/profile-completion'),
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
    retry: false,
  })

  return {
    isProfileCompleted: data?.isProfileCompleted ?? false,
    isLoading: isAuthenticated && isLoading,
  }
}
